/**
 * TodayHousePage — Today. The family brief.
 *
 * Page order:
 *   1. Header — room label, greeting, ARA focal sentence, sunrise watercolour.
 *   2. What matters most — max 3 items from existing Residence data.
 *   3. Today's schedule — compact family timetable (dailyFamilySchedule).
 *   4. Today's chores — central tasks, grouped by member.
 *   5. Shopping — active items from the existing shopping list.
 *
 * Every checkbox writes to the ORIGINAL record (tasks / bills / shopping).
 * No Today-only completion store exists.
 */
import { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import { usePersistentState } from "./state/usePersistentState";
import {
  ResidenceLayout, EditorialSection, ModuleMark,
  TextLink, Caption, Colophon, Rule, FirstFiveWhisper, useLightPhase,
  RoomWatercolour,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";
import { prioritiseToday } from "./utils/todayPriority";
import { getUpcomingFamilyBirthdays } from "./utils/birthdayIntelligence";
import { TodaySchedule, TodayChores, TodayShopping, QuietCheck } from "./components/TodayBriefSections";

const COUNT_WORDS = ["No", "One", "Two", "Three"];

const localISO = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function TodayHousePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const state = useHomeOSState();
  const { household, firstFive, markFirstFiveCelebrated, updateTask, updateShoppingItem } = state;
  const [bills, setBills] = usePersistentState("money.bills", []);
  const residentFallback = (user?.name || user?.email || '').split(' ')[0].split('@')[0] || 'friend';
  const firstMemberName = state.members?.[0]?.name;
  const resident = firstMemberName || residentFallback;

  // Onboarding gate — a fresh resident starts in the Foyer of the setup flow.
  useEffect(() => {
    if (state.hydrated && household && !household.onboarded) {
      navigate('/home-os/setup', { replace: true });
    }
  }, [state.hydrated, household, navigate]);

  const { phase, greeting, closingHint } = useLightPhase(resident);

  /* ── 1 · What matters most — existing priority engine, extended sources ── */
  const priority = useMemo(() => prioritiseToday({
    documents:      state.documents || [],
    tasks:          state.tasks || [],
    cleaningTasks:  state.cleaningTasks || [],
    suppliesItems:  state.suppliesItems || [],
    moodCheckins:   state.moodCheckins || [],
    members:        state.members || [],
    bills:          bills || [],
    weeklyMealPlan: state.weeklyMealPlan || [],
  }), [state.documents, state.tasks, state.cleaningTasks, state.suppliesItems, state.moodCheckins, state.members, bills, state.weeklyMealPlan]);

  const completePriority = (item) => {
    const c = item.completion;
    if (!c) return;
    if (c.kind === 'task') {
      updateTask(c.id, { status: 'Done' });
      toast.success(c.assignee ? `Thanks, ${c.assignee} — you've contributed to the family today.` : "Done — taken off the list.");
    } else if (c.kind === 'bill') {
      setBills(bs => bs.map(b => b.id === c.id ? { ...b, status: 'Paid' } : b));
      toast.success("Marked paid — the Study is up to date.");
    }
  };

  /* ── 2 · Schedule — existing dailyFamilySchedule + a birthday only if today ── */
  const scheduleEvents = useMemo(
    () => (state.dailyFamilySchedule || []).filter(e => e.status !== 'done'),
    [state.dailyFamilySchedule]
  );
  const birthdayNote = useMemo(() => {
    const b = getUpcomingFamilyBirthdays(state.members || []).find(x => x.daysUntil === 0);
    return b ? `${b.memberName}'s birthday is today.` : null;
  }, [state.members]);

  /* ── 3 · Chores — central tasks, due today / overdue / active today ── */
  const todayISO = localISO();
  const choreGroups = useMemo(() => {
    const members = state.members || [];
    const active = (state.tasks || []).filter(t =>
      t.category === 'Chores' &&
      !['Done', 'Ara suggested', 'Paused'].includes(t.status) &&
      (!t.dueDate || t.dueDate <= todayISO)
    );
    const groups = [];
    for (const t of active) {
      const member = members.find(m => m.id === t.assignedTo || m.name === t.assignedTo);
      const key = member?.id || 'household';
      const name = member ? (member.preferredName || member.name).split(' ')[0] : 'Household';
      let g = groups.find(x => x.key === key);
      if (!g) { g = { key, name, memberFirstName: member ? name : null, chores: [] }; groups.push(g); }
      const overdue = t.dueDate && t.dueDate < todayISO;
      g.chores.push({ ...t, dueLabel: overdue ? 'gently overdue' : t.dueDate === todayISO ? 'due today' : null });
    }
    return groups;
  }, [state.tasks, state.members, todayISO]);

  const completeChore = (t, group) => {
    updateTask(t.id, { status: 'Done' });
    toast.success(group.memberFirstName
      ? `Thanks, ${group.memberFirstName} — you've contributed to the family today.`
      : "Thank you for contributing to our family.");
  };

  /* ── 4 · Shopping — existing shopping list, active items only ── */
  const openShopping = useMemo(() => (state.shoppingList || []).filter(it => !it.checked), [state.shoppingList]);
  const shownShopping = openShopping.slice(0, 8);
  const completeShopping = (it) => {
    updateShoppingItem(it.id, { checked: true });
    toast.success("Ticked off the list.");
  };

  const today = new Date().toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long' });

  /* ARA focal sentence — judgement, not statistics. */
  const n = priority.items.length;
  const attentionCopy = n === 0
    ? (household?.onboarded
        ? "Nothing urgent needs your attention today."
        : "Nothing needs your attention. The house is calm.")
    : n === 1
      ? "One thing needs your attention today."
      : `${COUNT_WORDS[n] || n} things need your attention today.`;

  const firstFiveComplete = firstFive && [
    firstFive.familyMember, firstFive.meal, firstFive.bill, firstFive.pantryItem, firstFive.document,
  ].every(Boolean);
  const showFirstFiveWhisper = firstFiveComplete && !firstFive.celebrated;
  useEffect(() => {
    if (showFirstFiveWhisper) {
      const t = setTimeout(() => markFirstFiveCelebrated(), 8000);
      return () => clearTimeout(t);
    }
  }, [showFirstFiveWhisper, markFirstFiveCelebrated]);

  const hasSchedule = scheduleEvents.length > 0 || Boolean(birthdayNote);

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="today" />} phase={phase} testid="today-house">
      {/* ── Header ────────────────────────────────────────────────────── */}
      <header className="relative pt-10 sm:pt-14 pb-6" data-testid="today-greeting">
        <RoomWatercolour art="today" />
        <div className="relative" style={{ zIndex: 1 }}>
          <Caption testid="today-date-line">Today · {today}</Caption>
          <h1
            className="mt-3 font-display text-[28px] sm:text-[34px] lg:text-[38px] leading-[1.1] tracking-[-0.01em]"
            style={{ color: 'var(--ara-ink)', fontWeight: 400 }}
            data-testid="brief-headline"
          >
            {greeting}.
          </h1>
          <p className="mt-3 prose-ara text-[15px] sm:text-[15.5px] max-w-[52ch]" data-testid="brief-standfirst">
            {attentionCopy}
          </p>
        </div>
      </header>

      {showFirstFiveWhisper && <FirstFiveWhisper testid="first-five-whisper" />}

      {/* ── What matters most ─────────────────────────────────────────── */}
      {priority.items.length > 0 ? (
        <section data-testid="brief-items" className="pb-4">
          <Caption className="mb-1">What matters most</Caption>
          <ol>
            {priority.items.map((item, i) => (
              <li
                key={`${item.source}-${i}`}
                className={`grid grid-cols-[auto_1fr_auto] items-baseline gap-x-5 gap-y-1 py-5 sm:py-6 ${i === 0 ? 'border-t' : ''} border-b`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`brief-item-${i}`}
              >
                {item.completion ? (
                  <QuietCheck
                    onCheck={() => completePriority(item)}
                    testid={`today-priority-check-${i}`}
                    label={`Mark ${item.title} done`}
                  />
                ) : (
                  <span className="text-[13px] tabular-nums" style={{ color: 'var(--ara-ink-soft)', fontWeight: 400 }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                )}
                <div className="min-w-0">
                  <ModuleMark module={item.module || 'neutral'} label={item.moduleLabel || ''} />
                  <p
                    className="mt-2 text-[18px] sm:text-[20px] leading-snug tracking-[-0.005em]"
                    style={{ color: 'var(--ara-ink)', fontWeight: 400 }}
                  >
                    {item.title}
                  </p>
                  {item.meta && (
                    <p className="mt-1 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                      {item.meta}
                    </p>
                  )}
                </div>
                {item.roomLink && (
                  <TextLink onClick={() => navigate(item.roomLink)} testid={`brief-item-${i}-open`}>
                    Open →
                  </TextLink>
                )}
              </li>
            ))}
          </ol>
          {priority.remaining > 0 && (
            <p className="mt-6 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
              {priority.remaining} smaller thing{priority.remaining === 1 ? '' : 's'} waiting in their rooms.
            </p>
          )}
        </section>
      ) : (
        <section className="pt-2 pb-8" data-testid="brief-empty">
          <Rule className="mb-6" />
          <p className="prose-ara text-[14.5px] italic" style={{ color: 'var(--ara-ink-soft)' }}>
            Nothing urgent needs your attention today.
          </p>
        </section>
      )}

      {/* ── Today's schedule ──────────────────────────────────────────── */}
      {hasSchedule && (
        <EditorialSection
          testid="today-schedule"
          moduleMark={{ module: 'family', label: 'The family' }}
          title="Today's schedule"
          subtitle="Where everyone needs to be, and when."
        >
          <TodaySchedule events={scheduleEvents} members={state.members || []} birthdayNote={birthdayNote} />
        </EditorialSection>
      )}

      {/* ── Today's chores ────────────────────────────────────────────── */}
      {choreGroups.length > 0 && (
        <EditorialSection
          testid="today-chores"
          moduleMark={{ module: 'family', label: 'Chores' }}
          title="Today's chores"
          aside={<TextLink onClick={() => navigate('/home-os/chores')} testid="today-chores-open-room">Open Chores →</TextLink>}
        >
          <TodayChores groups={choreGroups} onComplete={completeChore} />
        </EditorialSection>
      )}

      {/* ── Shopping ──────────────────────────────────────────────────── */}
      {openShopping.length > 0 && (
        <EditorialSection
          testid="today-shopping"
          moduleMark={{ module: 'pantry', label: 'Shopping' }}
          title="Still to remember"
          aside={<TextLink onClick={() => navigate('/home-os/supplies')} testid="today-shopping-open-pantry">View Pantry →</TextLink>}
        >
          <TodayShopping
            items={shownShopping}
            remaining={openShopping.length - shownShopping.length}
            onComplete={completeShopping}
          />
        </EditorialSection>
      )}

      <Colophon testid="today-colophon">
        ARA · Ngakuru Home · {today}
      </Colophon>

      {/* Legacy testid preserved for older regression: closing italic marker */}
      {closingHint && <span className="hidden" data-testid="brief-closing" aria-hidden>{closingHint}</span>}
    </ResidenceLayout>
  );
}
